import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { MeteringModem, MeteringModemFlat } from '../meteringModem/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '../utils';
import { GetMeteringModemsCommercialParams } from './types';

export const meteringModemCommercialApi = createApi({
  reducerPath: 'meteringModemCommercialApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/commercial-consumers/modems`,
  }),
  tagTypes: ['MeteringModemCommercial', 'MeteringModemsCommercial'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getMeteringModemsCommercialI: builder.infiniteQuery<
      MeteringModemFlat,
      (FiltersGrid & GetMeteringModemsCommercialParams) | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => [
        'MeteringModemsCommercial',
        'MeteringModemCommercial',
      ],
    }),
    getMeteringModemsCommercial: builder.query<
      MeteringModemFlat[],
      GetMeteringModemsCommercialParams | void
    >({
      query: params => ({
        url: '/query',
        method: 'GET',
        params,
      }),
      providesTags: () => [
        'MeteringModemsCommercial',
        'MeteringModemCommercial',
      ],
    }),
    createMeteringModemCommercial: builder.mutation<
      MeteringModem,
      MeteringModem
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: result => [
        'MeteringModemCommercial',
        { type: 'MeteringModemCommercial', id: result?.id },
      ],
    }),
    getMeteringModemCommercialById: builder.query<MeteringModem, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['MeteringModemCommercial'],
    }),
    updateMeteringModemCommercialById: builder.mutation<
      MeteringModem,
      MeteringModem
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data: data,
      }),
      invalidatesTags: result => [
        'MeteringModemCommercial',
        { type: 'MeteringModemCommercial', id: result?.id },
      ],
    }),
    deleteMeteringModemCommercialById: builder.mutation<MeteringModem, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'MeteringModemCommercial',
        { type: 'MeteringModemCommercial', id },
      ],
    }),
  }),
});

export const {
  useGetMeteringModemsCommercialQuery,
  useCreateMeteringModemCommercialMutation,
  useGetMeteringModemCommercialByIdQuery,
  useUpdateMeteringModemCommercialByIdMutation,
  useDeleteMeteringModemCommercialByIdMutation,
} = meteringModemCommercialApi;

export const useGetMeteringModemsCommercialInfiniteQuery =
  normalizeInfiniteQueryData(
    meteringModemCommercialApi.useGetMeteringModemsCommercialIInfiniteQuery,
  );

const getCachedModemCommercialByIdSelector = cachedItemByIdSelector(
  'meteringModemCommercialApi',
  'getMeteringModemsCommercial',
);

export const useGetCachedModemCommercialByIdSelector = id =>
  useSelector(getCachedModemCommercialByIdSelector(id));
